/**
 * diagnose_flow tool - Troubleshoot flow issues
 *
 * Analyzes flow runs, identifies errors, and provides fixes
 */

import { z } from "zod";
import { displayValue } from "../utils/format.js";
import type { FlowManagementApi } from "../api/flow-management.js";
import { logger } from "../utils/logger.js";

export const diagnoseFlowInputSchema = z.object({
  flowId: z.string().describe("The flow ID to diagnose"),
  runId: z.string().optional().describe("Specific run ID to analyze (defaults to most recent failed run)"),
  environment: z.string().optional().describe("Environment name or GUID"),
});

export type DiagnoseFlowInput = z.infer<typeof diagnoseFlowInputSchema>;

export const diagnoseFlowTool = {
  name: "diagnose_flow",
  description:
    "Diagnose issues with a Power Automate flow. Analyzes recent runs, identifies failures, and provides specific fixes. Use this when a flow fails or behaves unexpectedly.",
  inputSchema: {
    type: "object" as const,
    properties: {
      flowId: {
        type: "string",
        description: "The flow ID to diagnose",
      },
      runId: {
        type: "string",
        description: "Specific run ID to analyze (defaults to most recent failed run)",
      },
      environment: {
        type: "string",
        description: "Environment name or GUID",
      },
    },
    required: ["flowId"],
  },
};

// Common error patterns and their fixes
const ERROR_PATTERNS: Array<{
  pattern: RegExp;
  category: string;
  description: string;
  fixes: string[];
}> = [
  {
    pattern: /invalid.*connection|connection.*not found|unauthorized|401|403/i,
    category: "Connection Error",
    description: "The connection is invalid, expired, or lacks permissions",
    fixes: [
      "Re-authenticate the connection in Power Automate portal",
      "Check if the service account has required permissions",
      "Verify the connection is shared with the flow owner",
      "Try creating a new connection and updating the flow",
    ],
  },
  {
    pattern: /not found|404|does not exist|resource.*missing/i,
    category: "Resource Not Found",
    description: "A referenced resource (file, list, item, etc.) doesn't exist",
    fixes: [
      "Verify the resource path/ID is correct",
      "Check if the resource was deleted or moved",
      "Ensure the service account has read access",
      "Use dynamic content instead of hardcoded IDs",
    ],
  },
  {
    pattern: /timeout|timed out|request timeout|504|execution.*exceeded/i,
    category: "Timeout Error",
    description: "The operation took too long to complete",
    fixes: [
      "Enable async pattern for long-running operations",
      "Increase timeout settings on the action",
      "Break large operations into smaller batches",
      "Add retry policy with exponential backoff",
      "Consider using a child flow for heavy processing",
    ],
  },
  {
    pattern: /rate limit|throttl|429|too many requests/i,
    category: "Rate Limiting",
    description: "Too many requests to the service in a short time",
    fixes: [
      "Add delays between API calls",
      "Implement exponential backoff retry policy",
      "Reduce concurrency in Apply to Each loops",
      "Batch operations where possible",
      "Consider spreading load across time",
    ],
  },
  {
    pattern: /invalid.*json|parse.*error|unexpected token|malformed/i,
    category: "Data Format Error",
    description: "The data format is invalid or unexpected",
    fixes: [
      "Validate JSON schema before parsing",
      "Use Try-Catch to handle parse errors gracefully",
      "Check for null/empty values before processing",
      "Use coalesce() for default values",
      "Verify the source data format matches expectations",
    ],
  },
  {
    pattern: /expression.*invalid|cannot.*evaluate|syntax.*error/i,
    category: "Expression Error",
    description: "A flow expression has a syntax error or invalid reference",
    fixes: [
      "Check expression syntax in the formula bar",
      "Verify all referenced fields exist",
      "Use outputs() instead of body() for some connectors",
      "Wrap risky expressions in try/catch or coalesce()",
      "Test expressions with sample data first",
    ],
  },
  {
    pattern: /duplicate|already exists|conflict|409/i,
    category: "Duplicate/Conflict Error",
    description: "Trying to create something that already exists",
    fixes: [
      "Check if item exists before creating",
      "Use upsert pattern (get, then create or update)",
      "Add unique identifiers to prevent duplicates",
      "Handle the error gracefully and continue",
    ],
  },
  {
    pattern: /permission|access denied|forbidden|not authorized/i,
    category: "Permission Error",
    description: "Insufficient permissions for the operation",
    fixes: [
      "Check service account permissions",
      "Verify API permissions in Microsoft Entra",
      "Ensure connection has required scopes",
      "Request elevated permissions if needed",
      "Use a different connection with proper access",
    ],
  },
  {
    pattern: /quota|limit.*exceeded|storage.*full/i,
    category: "Quota Exceeded",
    description: "A service quota or limit has been reached",
    fixes: [
      "Check service limits and quotas",
      "Clean up old data or runs",
      "Upgrade service tier if needed",
      "Implement data archival strategy",
    ],
  },
  {
    pattern: /trigger.*condition|no.*data|empty.*result/i,
    category: "No Data/Trigger Issue",
    description: "The trigger condition wasn't met or returned no data",
    fixes: [
      "Verify trigger conditions are correct",
      "Check if source has matching data",
      "Review OData filters in trigger",
      "Test with broader conditions first",
    ],
  },
];

interface FailedActionInfo {
  name: string;
  errorCode: string;
  errorMessage: string;
  category: string;
  suggestedFixes: string[];
  httpStatus?: number;
  inputs?: string;
  outputs?: string;
}

interface DiagnosisResult {
  flowName: string;
  flowId: string;
  status: "healthy" | "failing" | "intermittent" | "no-history";
  /** The flow's own on/off state — a stopped flow explains an empty run history. */
  flowState?: string;
  recentRuns: {
    total: number;
    succeeded: number;
    failed: number;
    successRate: string;
  };
  errorTrend: {
    category: string;
    count: number;
  }[];
  failedRun?: {
    runId: string;
    startTime: string;
    failedActions: FailedActionInfo[]; // Now supports multiple
    errorCode: string;
    errorMessage: string;
    category: string;
    suggestedFixes: string[];
  };
  recommendations: string[];
  nextSteps: string[];
}

export async function handleDiagnoseFlow(
  api: FlowManagementApi,
  input: unknown
): Promise<{ text: string; structured?: Record<string, unknown> }> {
  const parsed = diagnoseFlowInputSchema.parse(input);

  logger.info({ flowId: parsed.flowId }, "Diagnosing flow");

  // Get flow details
  const flow = await api.getFlow(parsed.flowId, parsed.environment);
  const flowName = flow.properties.displayName;

  // Get recent runs
  const runs = await api.getFlowRuns(parsed.flowId, parsed.environment, 25);

  const succeeded = runs.filter(r => r.properties.status === "Succeeded").length;
  const failed = runs.filter(r => r.properties.status === "Failed").length;
  const total = runs.length;
  const successRate = total > 0 ? ((succeeded / total) * 100).toFixed(0) : "N/A";

  // Determine overall status. An empty run history is the ABSENCE of evidence,
  // not evidence of health: a stopped flow with no runs was being reported as
  // "Healthy ... running smoothly. No action needed.", which is the opposite of
  // what someone asking "why isn't my flow working?" needs to hear.
  const flowState = flow.properties.state;
  let status: DiagnosisResult["status"] = "healthy";
  if (total === 0) {
    status = "no-history";
  } else if (failed > 0 && succeeded === 0) {
    status = "failing";
  } else if (failed > 0) {
    status = "intermittent";
  }

  // Analyze error trends across all failed runs
  const errorCategories: Map<string, number> = new Map();
  const allFailedRuns = runs.filter(r => r.properties.status === "Failed");

  for (const run of allFailedRuns) {
    const errorMsg = run.properties.error?.message || "";
    let foundCategory = "Unknown Error";
    for (const pattern of ERROR_PATTERNS) {
      if (pattern.pattern.test(errorMsg)) {
        foundCategory = pattern.category;
        break;
      }
    }
    errorCategories.set(foundCategory, (errorCategories.get(foundCategory) || 0) + 1);
  }

  const errorTrend = Array.from(errorCategories.entries())
    .map(([category, count]) => ({ category, count }))
    .sort((a, b) => b.count - a.count);

  const result: DiagnosisResult = {
    flowName,
    flowId: parsed.flowId,
    status,
    recentRuns: { total, succeeded, failed, successRate: total > 0 ? `${successRate}%` : successRate },
    ...(flowState === undefined ? {} : { flowState }),
    errorTrend,
    recommendations: [],
    nextSteps: [],
  };

  // Find the failed run to analyze
  const failedRun = parsed.runId
    ? runs.find(r => r.name === parsed.runId)
    : runs.find(r => r.properties.status === "Failed");

  if (failedRun) {
    // Get detailed action information for the failed run
    const failedActionsInfo: FailedActionInfo[] = [];
    let primaryErrorCode = "Unknown";
    let primaryErrorMessage = failedRun.properties.error?.message || "No error message available";

    try {
      const actions = await api.getFlowRunActions(parsed.flowId, failedRun.name, parsed.environment);
      const failedActions = actions.filter(a => a.properties.status === "Failed");

      // Analyze ALL failed actions and fetch their actual inputs/outputs
      const ioFetches: Promise<void>[] = [];

      for (const action of failedActions) {
        const errorCode = action.properties.error?.code || "Unknown";
        const errorMessage = action.properties.error?.message || "Unknown error";

        // Match error to patterns
        let category = "Unknown Error";
        let suggestedFixes: string[] = ["Review the error message and action inputs"];

        for (const pattern of ERROR_PATTERNS) {
          if (pattern.pattern.test(errorMessage) || pattern.pattern.test(errorCode)) {
            category = pattern.category;
            suggestedFixes = pattern.fixes;
            break;
          }
        }

        const info: FailedActionInfo = {
          name: action.name,
          errorCode,
          errorMessage,
          category,
          suggestedFixes,
        };
        failedActionsInfo.push(info);

        // Fetch actual HTTP response body from outputsLink — this contains
        // the real API error (e.g., Jira's "API has been removed" message)
        // instead of just the generic Power Automate error wrapper.
        if (action.properties.outputsLink?.uri) {
          ioFetches.push(
            api.fetchResourceLink(action.properties.outputsLink.uri).then(
              (data) => {
                const d = data as Record<string, unknown>;
                // Extract HTTP status code
                if (d.statusCode) info.httpStatus = Number(d.statusCode);
                // Extract response body — the actual API error
                const body = d.body;
                if (body) {
                  const bodyStr = displayValue(body);
                  info.outputs = bodyStr.length > 800 ? bodyStr.slice(0, 800) + "…" : bodyStr;
                  // If the generic error was unhelpful, replace with the real error
                  if ((errorMessage === "Unknown error" || errorMessage.includes("An action failed")) && bodyStr.length > 5) {
                    info.errorMessage = bodyStr.slice(0, 300);
                    // Re-match error patterns with the real error body
                    for (const pattern of ERROR_PATTERNS) {
                      if (pattern.pattern.test(bodyStr)) {
                        info.category = pattern.category;
                        info.suggestedFixes = pattern.fixes;
                        break;
                      }
                    }
                  }
                }
              },
              () => { /* ignore fetch errors */ }
            )
          );
        }

        // Fetch inputs — shows what URL/body was sent (helps debug bad requests)
        if (action.properties.inputsLink?.uri) {
          ioFetches.push(
            api.fetchResourceLink(action.properties.inputsLink.uri).then(
              (data) => {
                const str = displayValue(data);
                info.inputs = str.length > 800 ? str.slice(0, 800) + "…" : str;
              },
              () => { /* ignore fetch errors */ }
            )
          );
        }
      }

      // Wait for all I/O fetches to complete
      if (ioFetches.length > 0) {
        await Promise.all(ioFetches);
      }

      if (failedActionsInfo.length > 0) {
        primaryErrorCode = failedActionsInfo[0]!.errorCode;
        primaryErrorMessage = failedActionsInfo[0]!.errorMessage;
      }
    } catch {
      // Actions API might not be available, use run-level error
    }

    // Match primary error to patterns
    let category = "Unknown Error";
    let suggestedFixes: string[] = ["Review the error message and action inputs", "Check the flow run details in Power Automate portal"];

    for (const pattern of ERROR_PATTERNS) {
      if (pattern.pattern.test(primaryErrorMessage) || pattern.pattern.test(primaryErrorCode)) {
        category = pattern.category;
        suggestedFixes = pattern.fixes;
        break;
      }
    }

    result.failedRun = {
      runId: failedRun.name,
      startTime: failedRun.properties.startTime,
      failedActions: failedActionsInfo,
      errorCode: primaryErrorCode,
      errorMessage: primaryErrorMessage,
      category,
      suggestedFixes,
    };
  }

  // Generate recommendations based on analysis
  if (status === "failing") {
    result.recommendations.push("Flow is consistently failing - immediate attention needed");
  } else if (status === "intermittent") {
    result.recommendations.push(`Flow has ${successRate}% success rate - investigate intermittent failures`);
  }

  if (flow.properties.state !== "Started") {
    result.recommendations.push(`Flow is currently ${flow.properties.state} - may need to be started`);
  }

  // Check for common issues in flow definition
  const definition = flow.properties.definition;
  const hasErrorHandling = JSON.stringify(definition).includes('"runAfter"') &&
                          JSON.stringify(definition).includes('"Failed"');

  if (!hasErrorHandling && status !== "healthy") {
    result.recommendations.push("Consider adding Try-Catch error handling pattern");
  }

  // Generate next steps
  if (result.failedRun) {
    result.nextSteps = [
      `Fix: Apply one of the suggested fixes for "${result.failedRun.category}"`,
      `Update the flow using update_flow with corrected action`,
      `Test: Run the flow again using run_flow`,
      `Verify: Check with get_runs that it succeeds`,
    ];
  } else if (status === "healthy") {
    result.nextSteps = [
      "Flow is healthy - no action needed",
      "Consider adding monitoring/alerting for proactive issue detection",
    ];
  }

  // Format enhanced output
  const statusEmoji =
    status === "healthy" ? "✅" : status === "failing" ? "❌" : status === "no-history" ? "❔" : "⚠️";
  const statusLabel =
    status === "healthy"
      ? "Healthy"
      : status === "failing"
        ? "Failing"
        : status === "no-history"
          ? "No run history"
          : "Intermittent";

  let output = `## ${flowName}\n\n`;
  output += `**Status:** ${statusEmoji} ${statusLabel}\n`;
  // "N/A%" is not a success rate. With no runs there is no rate to report.
  output += total > 0
    ? `**Success Rate:** ${successRate}% (${succeeded}/${total} runs)\n`
    : `**Success Rate:** no runs to measure\n`;
  if (result.flowState !== undefined) {
    output += `**Flow state:** ${result.flowState}\n`;
  }
  output += `\n`;

  // Show error trend if there are failures
  if (result.errorTrend.length > 0) {
    output += `### Error Trend (Last ${total} Runs)\n`;
    for (const trend of result.errorTrend) {
      const bar = "█".repeat(Math.min(10, Math.ceil(trend.count / failed * 10)));
      output += `  ${bar} ${trend.category}: ${trend.count} occurrence${trend.count > 1 ? "s" : ""}\n`;
    }
    output += `\n`;

    // Determine if transient or chronic
    if (failed === total && total >= 3) {
      output += `**Pattern:** 🔴 Chronic - All recent runs failing\n\n`;
    } else if (failed > 0 && failed < total) {
      output += `**Pattern:** 🟡 Intermittent - ${failed} of ${total} runs failing\n\n`;
    }
  }

  if (result.failedRun) {
    output += `### Last Failed Run\n`;
    output += `**Run ID:** ${result.failedRun.runId}\n`;
    output += `**Time:** ${result.failedRun.startTime}\n`;
    output += `**Category:** ${result.failedRun.category}\n\n`;

    // Show ALL failed actions
    if (result.failedRun.failedActions.length > 0) {
      output += `**Failed Actions (${result.failedRun.failedActions.length}):**\n`;
      for (const action of result.failedRun.failedActions) {
        output += `  ❌ **${action.name}** - ${action.category}`;
        if (action.httpStatus) output += ` (HTTP ${action.httpStatus})`;
        output += `\n`;
        output += `     Error: ${action.errorMessage.slice(0, 300)}${action.errorMessage.length > 300 ? "..." : ""}\n`;
        if (action.outputs && action.outputs !== action.errorMessage.slice(0, 300)) {
          output += `     Response: ${action.outputs.slice(0, 500)}${action.outputs.length > 500 ? "..." : ""}\n`;
        }
        if (action.inputs) {
          output += `     Request: ${action.inputs.slice(0, 300)}${action.inputs.length > 300 ? "..." : ""}\n`;
        }
        output += `     Fix: ${action.suggestedFixes[0]}\n`;
      }
      output += `\n`;
    } else {
      output += `**Error:** ${result.failedRun.errorMessage.slice(0, 150)}\n\n`;
    }

    output += `### Recommended Fixes\n`;
    for (let i = 0; i < Math.min(3, result.failedRun.suggestedFixes.length); i++) {
      output += `${i + 1}. ${result.failedRun.suggestedFixes[i]}\n`;
    }
    output += `\n`;

    output += `### Next Steps\n`;
    output += `1. Apply the fix above\n`;
    output += `2. Update the flow: \`update_flow flowId="${parsed.flowId}"\`\n`;
    output += `3. Test: \`test_flow flowId="${parsed.flowId}"\`\n`;
    output += `4. Verify: \`get_runs flowId="${parsed.flowId}"\`\n`;
  } else if (status === "healthy") {
    output += `### Status\n`;
    output += `Flow is running smoothly. No action needed.\n`;
  } else if (status === "no-history") {
    output += `### Status\n`;
    output += `This flow has no run history, so there is nothing to diagnose from — that is the absence of evidence, not evidence that it is healthy.\n`;
    if (result.flowState !== undefined && result.flowState !== "Started") {
      output += `\nIts state is **${result.flowState}**, so it is not running. If you expected runs, that is very likely why — start it with \`toggle_flow flowId="${parsed.flowId}"\`.\n`;
    } else {
      output += `\nIt is started, so either its trigger has not fired yet or runs have aged out of history. Check the trigger with \`get_flow flowId="${parsed.flowId}"\`.\n`;
    }
  }

  // Machine-parseable mirror of the prose, emitted as MCP structuredContent (#11).
  // DiagnosisResult already carries everything the prose renders (status, run stats,
  // error trend, failed-run drill-down, recommendations, next steps).
  const structured: Record<string, unknown> = { ...result };

  return { text: output, structured };
}
